import { isApiError } from './errors';

/**
 * Voorbeelddata
 * =============
 * Een paar endpoints uit `docs/02-api-contract.md` bestaan nog niet in de API.
 * Het scherm daarachter toont dan voorbeelddata - maar nooit ongemerkt:
 *
 * 1. **Alleen als het endpoint ontbreekt** (404 of 501). Geen verbinding, een
 *    time-out of een 500 is een echte fout en blijft zichtbaar; anders ziet
 *    iemand zonder netwerk verzonnen cijfers alsof ze echt zijn.
 * 2. **Alleen leesverkeer.** Een schrijfactie doet nooit alsof hij gelukt is.
 * 3. **Altijd gemarkeerd.** Het antwoord krijgt een onzichtbaar label; de schil
 *    zet boven elk scherm dat zulke data toont een melding, en de querycache
 *    bewaart het niet in IndexedDB.
 *
 * Zodra het endpoint bestaat verdwijnt de aanroep; elke plek staat naast een
 * `TODO(agent: api)` met het contract dat de UI verwacht.
 */
const SAMPLE = Symbol.for('smartkeur.sample-data');

type Tagged = { [SAMPLE]?: string };

/** Een kopie van `data`, gemarkeerd als voorbeelddata over `label` ("Dashboard"). */
export function markAsSample<T>(data: T, label: string): T {
    if (data === null || typeof data !== 'object') {
        return data;
    }

    const copy = (Array.isArray(data) ? [...data] : { ...data }) as T & object;

    Object.defineProperty(copy, SAMPLE, { value: label, enumerable: false });

    return copy;
}

/** Het label als dit voorbeelddata is, anders `null`. */
export function sampleLabelOf(data: unknown): string | null {
    if (data === null || typeof data !== 'object') {
        return null;
    }

    return (data as Tagged)[SAMPLE] ?? null;
}

/** Ontbreekt dit endpoint in de API? */
export function isMissingEndpoint(error: unknown): boolean {
    return isApiError(error) && (error.status === 404 || error.status === 501);
}

/**
 * Leest van de API; bestaat het endpoint nog niet, dan gemarkeerde
 * voorbeelddata. Elke andere fout komt gewoon door.
 */
export async function readOrSample<T>(
    label: string,
    read: () => Promise<T>,
    sample: () => T,
): Promise<T> {
    try {
        return await read();
    } catch (error) {
        if (!isMissingEndpoint(error)) {
            throw error;
        }

        return markAsSample(sample(), label);
    }
}
